import { Injectable, inject } from '@angular/core';
import { SupabaseService } from '../../../shared/services/supabase.service';
import { BrandSlug } from '../shared/brand-slug';
import { OrgId, UserId } from '../shared/ids';
import type { Organization, OrgMember, OrgRole, SubscriptionStatus } from './organization';
import { OrganizationRepository } from './organization-repository';

type OrgRow = {
  id: string;
  display_name: string | null;
  is_super_org: boolean | null;
  brands_own: string[] | null;
  brands_comp: string[] | null;
  subscription_status: string | null;
  subscription_ends_at: string | null;
};

type MemberRow = {
  org_id: string;
  user_id: string;
  org_level: string | null;
  name: string | null;
};

@Injectable()
export class SupabaseOrganizationRepository extends OrganizationRepository {
  private readonly supabase = inject(SupabaseService);

  async forUser(userId: UserId): Promise<{
    orgs: readonly Organization[];
    memberships: readonly OrgMember[];
  }> {
    const { data: memberRows, error: memberError } = await this.supabase.client
      .from('portal_org_member')
      .select('org_id, user_id, org_level, name')
      .eq('user_id', userId);
    if (memberError) throw memberError;

    const memberships = (memberRows as MemberRow[]).map(rowToMember);
    if (memberships.length === 0) return { orgs: [], memberships: [] };

    const orgIds = Array.from(new Set(memberships.map((m) => m.orgId)));
    const { data: orgRows, error: orgError } = await this.supabase.client
      .from('portal_org')
      .select(
        'id, display_name, is_super_org, brands_own, brands_comp, subscription_status, subscription_ends_at'
      )
      .in('id', orgIds);
    if (orgError) throw orgError;

    const orgs = (orgRows as OrgRow[]).map(rowToOrganization);
    return { orgs, memberships };
  }
}

function rowToOrganization(row: OrgRow): Organization {
  return {
    kind: 'organization',
    id: OrgId(row.id),
    displayName: row.display_name?.trim() || row.id,
    isSuperOrg: row.is_super_org === true,
    subscription: toSubscriptionStatus(row.subscription_status),
    subscriptionEndsAt: row.subscription_ends_at ? new Date(row.subscription_ends_at) : null,
    buckets: {
      own: toBrandSlugSet(row.brands_own),
      competitor: toBrandSlugSet(row.brands_comp),
    },
  };
}

function rowToMember(row: MemberRow): OrgMember {
  return {
    kind: 'org-member',
    userId: UserId(row.user_id),
    orgId: OrgId(row.org_id),
    displayName: row.name?.trim() || null,
    role: toOrgRole(row.org_level),
  };
}

function toOrgRole(raw: string | null): OrgRole {
  switch (raw) {
    case 'admin':
    case 'super-admin':
      return raw;
    default:
      return 'user';
  }
}

function toBrandSlugSet(raw: string[] | null): ReadonlySet<BrandSlug> {
  if (!Array.isArray(raw)) return new Set();
  const out = new Set<BrandSlug>();
  for (const s of raw) {
    if (isNonEmptyString(s)) out.add(BrandSlug(s));
  }
  return out;
}

function toSubscriptionStatus(raw: string | null): SubscriptionStatus {
  switch (raw) {
    case 'active':
    case 'paused':
    case 'paused_unpaid':
    case 'pending':
      return raw;
    default:
      return 'active';
  }
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}
